import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, Download, Mail, Video, Play, Clock, Check, Disc } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { TrackCard } from '@/components/TrackCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Album card for library with download zip option
const AlbumLibraryCard = ({ purchase }) => {
  const album = purchase.details;
  
  const handleDownloadZip = (format = 'mp3') => {
    const url = format === 'wav' ? purchase.download_url_wav : purchase.download_url;
    if (url) {
      window.open(url, '_blank');
      toast.success(`Album ZIP download started (${format.toUpperCase()})!`);
    } else {
      toast.error('Download link not available');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-all group"
    >
      {/* Album Cover */}
      <div className="relative aspect-square bg-stone-800">
        {album.cover_url ? (
          <img
            src={album.cover_url}
            alt={album.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Disc className="w-16 h-16 text-muted-foreground" />
          </div>
        )}
        
        {/* Album Badge */}
        <div className="absolute top-3 left-3 px-3 py-1 bg-purple-500/90 text-white text-xs font-bold rounded-full flex items-center gap-1">
          <Disc className="w-3 h-3" />
          ALBUM
        </div>
        
        {/* Track Count */}
        <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/60 rounded-full text-xs font-bold text-white">
          {album.track_ids?.length || 0} tracks
        </div>
      </div>

      {/* Album Info */}
      <div className="p-4">
        <h3 className="font-bold text-foreground mb-1 truncate">{album.title}</h3>
        <p className="text-sm text-muted-foreground mb-4 truncate">{album.artist}</p>
        
        {/* Download Buttons */}
        <div className="space-y-2">
          <button
            onClick={() => handleDownloadZip('mp3')}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-500 hover:to-pink-500 transition-all flex items-center justify-center gap-2"
            data-testid={`download-album-zip-${album.id}`}
          >
            <Download className="w-4 h-4" />
            Download Album (MP3 ZIP)
          </button>
          {purchase.download_url_wav && (
            <button
              onClick={() => handleDownloadZip('wav')}
              className="w-full py-2 bg-stone-700 text-white rounded-xl hover:bg-stone-600 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Download className="w-3 h-3" />
              Download WAV ZIP
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const Library = () => {
  const [purchases, setPurchases] = useState([]);
  const [videoCourses, setVideoCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [activeTab, setActiveTab] = useState('music');

  useEffect(() => {
    const savedEmail = localStorage.getItem('user_email');
    if (savedEmail) {
      setEmail(savedEmail);
      localStorage.setItem('purchase_email', savedEmail);
      loadPurchases(savedEmail);
    } else {
      setLoading(false);
      setShowEmailPrompt(true);
    }
  }, []);

  const loadPurchases = async (userEmail) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/purchases/email/${userEmail}`);
      
      // Separate music and video purchases
      const musicPurchases = [];
      const videoIds = [];

      for (const purchase of response.data) {
        if (purchase.item_type === 'video_course') {
          videoIds.push(purchase.item_id);
        } else {
          // Get music details
          try {
            const endpoint = purchase.item_type === 'track' ? 'tracks' : 'albums';
            const itemResponse = await axios.get(`${API}/${endpoint}/${purchase.item_id}`);
            musicPurchases.push({
              ...purchase,
              details: itemResponse.data,
            });
          } catch (error) {
            console.error('Error loading item details:', error);
            musicPurchases.push(purchase);
          }
        }
      }
      
      setPurchases(musicPurchases);

      // Load video course details
      if (videoIds.length > 0) {
        const coursePromises = videoIds.map(id => 
          axios.get(`${API}/video-courses/${id}`).catch(() => null)
        );
        const courseResponses = await Promise.all(coursePromises);
        setVideoCourses(courseResponses.filter(r => r).map(r => r.data));
      }

    } catch (error) {
      console.error('Error loading purchases:', error);
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!emailInput || !emailInput.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    localStorage.setItem('user_email', emailInput);
    localStorage.setItem('purchase_email', emailInput);
    setEmail(emailInput);
    setShowEmailPrompt(false);
    loadPurchases(emailInput);
  };

  const handleChangeEmail = () => {
    setShowEmailPrompt(true);
    setEmailInput('');
  };

  const getWatchProgress = (courseId) => {
    const savedProgress = localStorage.getItem(`video_progress_${courseId}`);
    if (!savedProgress) return { completed: 0, total: 0 };
    
    const progress = JSON.parse(savedProgress);
    const completed = Object.values(progress).filter(p => p.completed || p.percentage >= 90).length;
    return { completed, total: Object.keys(progress).length };
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="library-loading">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (showEmailPrompt) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20" data-testid="email-prompt">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-3xl p-12 text-center"
        >
          <Music className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl font-bold font-display text-foreground mb-4">
            Access Your Library
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Enter the email address you used for your purchases
          </p>
          <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
            <div className="relative mb-6">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="your@email.com"
                className="w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                data-testid="library-email-input"
              />
            </div>
            <button
              type="submit"
              className="w-full px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-105"
              data-testid="library-email-submit-btn"
            >
              View My Library
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const hasContent = purchases.length > 0 || videoCourses.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-display text-foreground mb-2" data-testid="library-title">
              My Library
            </h1>
            <p className="text-muted-foreground">
              Signed in as: <span className="text-foreground font-semibold">{email}</span>
            </p>
          </div>
          <button
            onClick={handleChangeEmail}
            className="px-6 py-3 text-muted-foreground hover:text-foreground border border-border hover:border-primary/50 rounded-full transition-all"
            data-testid="change-email-btn"
          >
            Change Email
          </button>
        </div>

        {!hasContent ? (
          <div className="text-center py-20" data-testid="empty-library">
            <Music className="w-20 h-20 text-muted-foreground mx-auto mb-6 opacity-50" />
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Your library is empty
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Purchase some music or video courses to start building your collection!
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/"
                className="px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-105"
              >
                Browse Music
              </Link>
              <Link
                to="/videos"
                className="px-8 py-4 bg-card border border-border hover:border-primary/50 text-foreground rounded-full font-bold text-lg transition-all hover:scale-105"
              >
                Browse Videos
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setActiveTab('music')}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                  activeTab === 'music'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-foreground hover:border-primary/50'
                }`}
              >
                <Music className="w-5 h-5" />
                Music ({purchases.length})
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                  activeTab === 'videos'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-foreground hover:border-primary/50'
                }`}
              >
                <Video className="w-5 h-5" />
                Video Courses ({videoCourses.length})
              </button>
            </div>

            {/* Music Tab */}
            {activeTab === 'music' && (
              <div>
                {purchases.length === 0 ? (
                  <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
                    <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">No music purchases yet</p>
                    <Link to="/" className="text-primary hover:underline mt-2 inline-block">
                      Browse music
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {purchases.map((purchase) => {
                      if (!purchase.details) return null;
                      
                      // Use AlbumLibraryCard for albums
                      if (purchase.item_type === 'album') {
                        return (
                          <AlbumLibraryCard
                            key={purchase.id}
                            purchase={purchase}
                          />
                        );
                      }
                      
                      // Use TrackCard for tracks
                      const trackWithDownload = {
                        ...purchase.details,
                        download_url: purchase.download_url,
                      };
                      
                      return (
                        <TrackCard
                          key={purchase.id}
                          track={trackWithDownload}
                          isPurchased={true}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Videos Tab */}
            {activeTab === 'videos' && (
              <div>
                {videoCourses.length === 0 ? (
                  <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
                    <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">No video courses purchased yet</p>
                    <Link to="/videos" className="text-primary hover:underline mt-2 inline-block">
                      Browse video courses
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videoCourses.map((course) => {
                      const progress = getWatchProgress(course.id);
                      const totalVideos = course.videos?.length || course.video_count || 0;
                      
                      return (
                        <motion.div
                          key={course.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-all group"
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-stone-800">
                            {course.thumbnail_url ? (
                              <img
                                src={course.thumbnail_url}
                                alt={course.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Video className="w-12 h-12 text-muted-foreground" />
                              </div>
                            )}
                            
                            {/* Play Overlay */}
                            <Link
                              to={`/watch/${course.id}`}
                              className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                                <Play className="w-8 h-8 text-primary-foreground ml-1" fill="currentColor" />
                              </div>
                            </Link>

                            {/* Progress Badge */}
                            {progress.completed > 0 && (
                              <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/60 rounded-full text-xs font-bold text-white flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                {progress.completed}/{totalVideos}
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-5">
                            <h3 className="text-lg font-bold text-foreground mb-2">
                              {course.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                              <span className="flex items-center gap-1">
                                <Video className="w-4 h-4" />
                                {totalVideos} videos
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatDuration(course.total_duration || 0)}
                              </span>
                            </div>

                            {/* Progress Bar */}
                            {totalVideos > 0 && (
                              <div className="mb-4">
                                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                  <span>Progress</span>
                                  <span>{Math.round((progress.completed / totalVideos) * 100)}%</span>
                                </div>
                                <div className="h-2 bg-background rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full transition-all"
                                    style={{ width: `${(progress.completed / totalVideos) * 100}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3">
                              <Link
                                to={`/watch/${course.id}`}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold transition-colors"
                              >
                                <Play className="w-4 h-4" />
                                {progress.completed > 0 ? 'Continue' : 'Start'} Watching
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};
